import React, { useState, useEffect } from "react";
import styles from "./RegisterSede.module.css";
import axiosInstance from "../../api/axiosConfig";
import { Link, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const sedeTypeOptions = [
  "Clínica", "Hospital", "Centro médico", "Policlínico", "Consultorio",
  "Laboratorio", "Centro de diagnóstico", "Centro de rehabilitación", "Otro",
];

const serviceOptions = [
  "Tópico de enfermería", "Laboratorio clínico", "Diagnóstico por imágen",
  "Ambulancia", "Centro de vacunación", "Consultas generales", "Emergencias",
];

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const RegisterSede = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("sedeRegistrationData");
    const initial = {
      reason_social: "",
      name: "",
      ruc: "",
      contact_person: "",
      charges: "",
      type_of_service: "",
      type_of_sede: "",
      num_consultorios: "",
      country: "",
      department: "",
      province: "",
      district: "",
      address: "",
      reference: "",
      email: "",
      phone: "",
      website: "",
      password: "",
      confirm_password: "",
      termsAccepted: false,
      termsAcceptedAt: null,
    };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.timestamp && parsed.data) {
          if (new Date().getTime() - parsed.timestamp < 10 * 60 * 1000) {
            return { ...initial, ...parsed.data };
          }
          localStorage.removeItem("sedeRegistrationData");
        }
      } catch {}
    }
    return initial;
  });

  useEffect(() => {
    localStorage.setItem(
      "sedeRegistrationData",
      JSON.stringify({ data: formData, timestamp: new Date().getTime() })
    );
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      termsAccepted: e.target.checked,
      termsAcceptedAt: e.target.checked ? new Date() : null,
    }));
  };

  const goNext = (e) => {
    e.preventDefault();
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert("Debes aceptar los términos y condiciones");
      return;
    }
    if (formData.password !== formData.confirm_password) {
      alert("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post("/api/register-sede", formData);
      localStorage.removeItem("sedeRegistrationData");
      navigate("/registro/sede-exitosa");
    } catch (error) {
      console.error("Error al enviar el formulario:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerInner}>
          <div className={styles.breadcrumb}>
            <Link to="/">Inicio</Link> / <span>Registro de Sede / Clínica</span>
          </div>

          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              <h1>Registro de Sede</h1>
              <p>Registrá tu clínica o sede para ofrecer servicios de salud en la plataforma.</p>
            </div>
            <span className={styles.loginHint}>
              ¿Ya tenés cuenta? <Link to="/login">Iniciá sesión →</Link>
            </span>
          </div>

          {/* Stepper */}
          <div className={styles.stepper}>
            <div className={`${styles.stepItem} ${step >= 1 ? styles.active : ""}`}>
              <div className={styles.stepCircle}>1</div>
              Datos de la sede
            </div>
            <div className={`${styles.stepLine} ${step >= 2 ? styles.doneLine : ""}`} />
            <div className={`${styles.stepItem} ${step === 2 ? styles.active : ""}`}>
              <div className={styles.stepCircle}>2</div>
              Contacto y acceso
            </div>
          </div>
        </div>
      </div>

      {/* ── Contenido ── */}
      <div className={styles.content}>

        {/* ────── PASO 1 ────── */}
        {step === 1 && (
          <form onSubmit={goNext}>
            <div className={styles.card}>
              <p className={styles.stepLabel}>PASO 1 DE 2</p>
              <h2 className={styles.cardTitle}>Datos de la sede</h2>
              <p className={styles.cardSubtitle}>Información institucional de tu organización.</p>

              <div className={styles.formGrid}>
                {/* Fila 1 */}
                <div className={styles.field}>
                  <label className={styles.label}>RAZÓN SOCIAL*</label>
                  <input className={styles.input} name="reason_social" value={formData.reason_social} onChange={handleChange} placeholder="Razón social oficial" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>NOMBRE COMERCIAL*</label>
                  <input className={styles.input} name="name" value={formData.name} onChange={handleChange} placeholder="Como aparecerá en la plataforma" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>RUC</label>
                  <input className={styles.input} name="ruc" value={formData.ruc} onChange={handleChange} placeholder="20XXXXXXXXX" />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>PERSONA DE CONTACTO</label>
                  <input className={styles.input} name="contact_person" value={formData.contact_person} onChange={handleChange} placeholder="Nombre completo" />
                </div>

                {/* Fila 2 */}
                <div className={styles.field}>
                  <label className={styles.label}>CARGO</label>
                  <input className={styles.input} name="charges" value={formData.charges} onChange={handleChange} placeholder="Ej. Gerente General" />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>SERVICIOS QUE BRINDA*</label>
                  <select className={styles.select} name="type_of_service" value={formData.type_of_service} onChange={handleChange} required>
                    <option value="">Seleccionar</option>
                    {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>TIPO DE SEDE</label>
                  <select className={styles.select} name="type_of_sede" value={formData.type_of_sede} onChange={handleChange}>
                    <option value="">Seleccionar</option>
                    {sedeTypeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>N.° DE CONSULTORIOS</label>
                  <input className={styles.input} type="number" name="num_consultorios" value={formData.num_consultorios} onChange={handleChange} placeholder="0" min="0" />
                </div>

                {/* Fila 3 */}
                <div className={styles.field}>
                  <label className={styles.label}>PAÍS</label>
                  <input className={styles.input} name="country" value={formData.country} onChange={handleChange} placeholder="Perú" />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>DEPARTAMENTO</label>
                  <input className={styles.input} name="department" value={formData.department} onChange={handleChange} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>PROVINCIA</label>
                  <input className={styles.input} name="province" value={formData.province} onChange={handleChange} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>DISTRITO</label>
                  <input className={styles.input} name="district" value={formData.district} onChange={handleChange} />
                </div>

                {/* Fila 4 - campos anchos */}
                <div className={styles.fieldHalf}>
                  <label className={styles.label}>DIRECCIÓN</label>
                  <input className={styles.input} name="address" value={formData.address} onChange={handleChange} placeholder="Av. Ejemplo 123, Piso 2" />
                </div>
                <div className={styles.fieldHalf}>
                  <label className={styles.label}>REFERENCIA</label>
                  <input className={styles.input} name="reference" value={formData.reference} onChange={handleChange} placeholder="Frente al parque central" />
                </div>
              </div>

              <p className={styles.required}>* Campos obligatorios</p>

              <div className={styles.cardFooter}>
                <Link to="/" className={styles.btnBack}>← Volver al inicio</Link>
                <button type="submit" className={styles.btnNext}>Siguiente →</button>
              </div>
            </div>
          </form>
        )}

        {/* ────── PASO 2 ────── */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className={styles.card}>
              <p className={styles.stepLabel}>PASO 2 DE 2</p>
              <h2 className={styles.cardTitle}>Contacto y acceso</h2>
              <p className={styles.cardSubtitle}>Información de contacto y credenciales de acceso.</p>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>CORREO ELECTRÓNICO*</label>
                  <input className={styles.input} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="sede@email.com" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>TELÉFONO*</label>
                  <input className={styles.input} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+51 999 999 999" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>SITIO WEB</label>
                  <input className={styles.input} type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://..." />
                </div>
                <div />
              </div>

              <div className={styles.formGrid2}>
                <div className={styles.field}>
                  <label className={styles.label}>CONTRASEÑA*</label>
                  <div className={styles.passwordWrap}>
                    <input className={styles.input} type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="Mínimo 8 caracteres" required minLength={8} />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword((v) => !v)}>
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>CONFIRMAR CONTRASEÑA*</label>
                  <div className={styles.passwordWrap}>
                    <input className={styles.input} type={showConfirm ? "text" : "password"} name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="Repetir contraseña" required minLength={8} />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirm((v) => !v)}>
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                </div>
              </div>

              <p className={styles.required}>* Campos obligatorios</p>

              <div className={styles.cardFooter}>
                <button type="button" className={styles.btnBack} onClick={() => { setStep(1); window.scrollTo({ top: 0 }); }}>← Atrás</button>
                <div className={styles.cardFooterRight}>
                  <label className={styles.checkRow}>
                    <input type="checkbox" checked={formData.termsAccepted} onChange={handleCheckboxChange} required />
                    Acepto los <Link to="/terminos-condiciones">términos y condiciones</Link> de Vitalclic *
                  </label>
                  <button type="submit" className={styles.btnSubmit} disabled={loading}>
                    {loading
                      ? <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                      : "✓ REGISTRARME"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

      </div>
    </div>
  );
};

export default RegisterSede;
